import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { api, useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import {
  Shield, LogOut, RefreshCw, Send, Copy, Download, Clock,
  ClipboardList, CheckCircle2, Hourglass, FileCheck, Eye
} from "lucide-react";

const STATUS = {
  pending:     { label: "Awaiting Insured",     color: "bg-amber-50 text-amber-700" },
  in_progress: { label: "Insured In Progress",  color: "bg-blue-50 text-blue-700" },
  completed:   { label: "Under Carrier Review", color: "bg-purple-50 text-purple-700" },
  reviewed:    { label: "Under Carrier Review", color: "bg-purple-50 text-purple-700" },
  finalized:   { label: "Report Ready",         color: "bg-green-50 text-green-700" },
};

const TYPE_LABELS = {
  commercial_property: "Commercial Property", general_liability: "General Liability",
  workers_comp: "Workers' Comp", commercial_auto: "Commercial Auto", habitational: "Habitational",
};

export default function BrokerPortal() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get("/broker/inspections");
      setData(res.data);
    } catch (e) {
      toast.error("Failed to load your book");
    }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const resend = async (id) => {
    setBusy(id);
    try {
      await api.post(`/broker/inspections/${id}/resend`);
      toast.success("Invitation resent to the insured");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Resend failed");
    }
    setBusy("");
  };

  const copyLink = (url) => {
    navigator.clipboard.writeText(url);
    toast.success("Survey link copied");
  };

  const downloadReport = async (id, insuredName) => {
    setBusy(id);
    try {
      const res = await api.get(`/broker/inspections/${id}/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `RiskSurvey_Report_${insuredName.replace(/ /g, "_")}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error("Report download failed");
    }
    setBusy("");
  };

  const canResend = (insp) => {
    if (!["pending", "in_progress"].includes(insp.status)) return false;
    if (!insp.last_broker_resend_at) return true;
    return Date.now() - new Date(insp.last_broker_resend_at).getTime() > 24 * 3600 * 1000;
  };

  const insps = data?.inspections || [];
  const stats = [
    { label: "Total Orders", value: insps.length, icon: ClipboardList, color: "text-navy" },
    { label: "Awaiting Insured", value: insps.filter(i => i.status === "pending").length, icon: Hourglass, color: "text-amber-600" },
    { label: "In Progress", value: insps.filter(i => i.status === "in_progress").length, icon: Clock, color: "text-blue-600" },
    { label: "Reports Ready", value: insps.filter(i => i.status === "finalized").length, icon: FileCheck, color: "text-green-600" },
  ];

  return (
    <div className="min-h-screen bg-slate-50" data-testid="broker-portal">
      <div className="bg-navy text-white px-6 py-4 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 bg-white/20 rounded-lg flex items-center justify-center">
            <Shield className="w-4 h-4 text-white" />
          </div>
          <div>
            <span className="font-heading font-bold text-white">RiskSurvey.ai</span>
            <span className="ml-2 text-xs text-white/50 bg-white/10 px-2 py-0.5 rounded-full">Broker Portal</span>
            {data?.carrier_name && <span className="ml-2 text-xs text-white/40">{data.carrier_name}</span>}
          </div>
        </div>
        <div className="flex items-center gap-4">
          <span className="text-sm text-white/60">{user?.name}</span>
          <button data-testid="broker-logout-btn" onClick={() => { logout(); navigate("/login"); }}
            className="flex items-center gap-1.5 text-white/60 hover:text-white text-sm transition-colors">
            <LogOut className="w-4 h-4" /> Sign out
          </button>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 py-8 space-y-6">
        <div>
          <h1 className="font-heading text-2xl font-bold text-slate-800">Your Book</h1>
          <p className="text-slate-500 text-sm mt-0.5">Track inspection status for your insureds and nudge non-responders</p>
        </div>

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {stats.map(s => (
            <div key={s.label} className="bg-white border border-slate-200 rounded-xl p-4" data-testid={`broker-stat-${s.label.toLowerCase().replace(/ /g, "-")}`}>
              <div className="flex items-center justify-between">
                <span className="text-xs font-semibold text-slate-400 uppercase tracking-wider">{s.label}</span>
                <s.icon className={`w-4 h-4 ${s.color}`} />
              </div>
              <p className={`font-heading text-2xl font-bold mt-1 ${s.color}`}>{s.value}</p>
            </div>
          ))}
        </div>

        <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden shadow-sm">
          {loading ? (
            <div className="py-16 text-center"><RefreshCw className="w-6 h-6 text-navy animate-spin mx-auto" /></div>
          ) : insps.length === 0 ? (
            <div className="py-16 text-center">
              <ClipboardList className="w-10 h-10 text-slate-200 mx-auto mb-3" />
              <p className="text-slate-400 text-sm">No inspections on your book yet</p>
              <p className="text-slate-300 text-xs mt-1">Orders placed with your email as broker will appear here</p>
            </div>
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs text-slate-400 uppercase tracking-wider border-b border-slate-100">
                  <th className="px-4 py-3 font-semibold">Insured</th>
                  <th className="px-4 py-3 font-semibold hidden md:table-cell">Coverage</th>
                  <th className="px-4 py-3 font-semibold">Status</th>
                  <th className="px-4 py-3 font-semibold hidden lg:table-cell">Pending</th>
                  <th className="px-4 py-3 text-right font-semibold">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {insps.map(insp => {
                  const st = STATUS[insp.status] || STATUS.pending;
                  return (
                    <tr key={insp.id} className="hover:bg-slate-50 transition-colors" data-testid={`broker-row-${insp.id}`}>
                      <td className="px-4 py-3">
                        <p className="font-medium text-slate-800">{insp.insured_name}</p>
                        <p className="text-xs text-slate-400">{insp.policy_number || insp.insured_email}</p>
                      </td>
                      <td className="px-4 py-3 hidden md:table-cell text-slate-600">{TYPE_LABELS[insp.inspection_type] || insp.inspection_type}</td>
                      <td className="px-4 py-3">
                        <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${st.color}`} data-testid={`broker-status-${insp.id}`}>{st.label}</span>
                      </td>
                      <td className="px-4 py-3 hidden lg:table-cell text-xs text-slate-500">
                        {insp.days_pending != null ? `${insp.days_pending}d` : "—"}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-1.5 justify-end">
                          {["pending", "in_progress"].includes(insp.status) && (
                            <button
                              data-testid={`broker-resend-btn-${insp.id}`}
                              disabled={busy === insp.id || !canResend(insp)}
                              title={canResend(insp) ? "Resend invitation to insured" : "Resent within the last 24h"}
                              onClick={() => resend(insp.id)}
                              className="flex items-center gap-1 px-2.5 py-1 bg-navy/10 text-navy hover:bg-navy hover:text-white rounded-md text-xs font-medium transition-colors disabled:opacity-40 disabled:cursor-not-allowed">
                              {busy === insp.id ? <RefreshCw className="w-3 h-3 animate-spin" /> : <Send className="w-3 h-3" />} Resend
                            </button>
                          )}
                          {insp.survey_url && (
                            <button data-testid={`broker-copy-link-btn-${insp.id}`} onClick={() => copyLink(insp.survey_url)}
                              className="flex items-center gap-1 px-2.5 py-1 bg-slate-100 text-slate-600 hover:bg-slate-600 hover:text-white rounded-md text-xs font-medium transition-colors">
                              <Copy className="w-3 h-3" /> Link
                            </button>
                          )}
                          {insp.status === "finalized" && (
                            <button data-testid={`broker-download-btn-${insp.id}`} disabled={busy === insp.id}
                              onClick={() => downloadReport(insp.id, insp.insured_name)}
                              className="flex items-center gap-1 px-2.5 py-1 bg-green-50 text-green-700 hover:bg-green-600 hover:text-white rounded-md text-xs font-medium transition-colors">
                              <Download className="w-3 h-3" /> Report
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>

        <p className="text-xs text-slate-400 flex items-center gap-1.5">
          <Eye className="w-3.5 h-3.5" />
          {data?.show_links
            ? "Your carrier has enabled survey link visibility — remember the insured must complete their own attestation."
            : "Survey links are sent directly to insureds to preserve attestation integrity. Use Resend to nudge non-responders."}
        </p>
      </div>
    </div>
  );
}
